/**
 * What a draw has historically returned per euro staked.
 *
 * This is the one place in the application where a number can legitimately inform a
 * decision about WHETHER to play — never about which numbers to play. A jackpot that
 * has rolled over contains the stakes of earlier draws, which tonight's player did
 * not pay, so the same ticket price buys a larger pool.
 *
 * The effect is measured, not reasoned: `npm run rtp` splits 1 113 Loto draws by the
 * announced rank-1 prize and finds 41 % return in the bottom third against 65 % in
 * the top. It is only available where the operator publishes the announced jackpot
 * on every draw. EuroMillions and EuroDreams publish it only when a French player
 * wins, so grouping by it there would condition on having won and produce a return
 * above 100 % — an artefact, not a finding.
 *
 * Every band is below 100 %. This narrows the loss; it never removes it.
 */
import { GAMES } from './games';
import type { GameKey } from './games';

export interface ReturnEstimate {
  /** Observed return for draws with a jackpot of this size, as a fraction. */
  rtp: number;
  /** Lower bound of the band, in cents. */
  fromCents: number;
  /** Upper bound, or null for the top band. */
  toCents: number | null;
  /** How this band compares with the game's all-draw average. */
  deltaVsAverage: number;
}

/**
 * The band a given announced jackpot falls into.
 * Returns null when the game publishes no usable jackpot, or when none is known.
 */
export function returnForJackpot(game: GameKey, jackpotCents: number | null): ReturnEstimate | null {
  const g = GAMES[game];
  const bands = g.jackpotBands;
  if (bands === null || jackpotCents === null || jackpotCents <= 0) return null;

  for (let i = bands.length - 1; i >= 0; i--) {
    const band = bands[i];
    if (band === undefined) continue;
    if (jackpotCents >= band.fromCents) {
      const next = bands[i + 1];
      return {
        rtp: band.rtp,
        fromCents: band.fromCents,
        toCents: next?.fromCents ?? null,
        deltaVsAverage: band.rtp - g.measuredRtp,
      };
    }
  }
  return null;
}

/** Expected value of one grid at this draw, in cents. Always negative. */
export function expectedValueCents(game: GameKey, jackpotCents: number | null): number {
  const g = GAMES[game];
  const rtp = returnForJackpot(game, jackpotCents)?.rtp ?? g.measuredRtp;
  return Math.round(g.price * 100 * (rtp - 1));
}
